<template>
  <PreAuthPage wide>
    <div class="sh-card__head">
      <LogoTile :branding="branding" :alt="tenantName" :size="44" />
      <h1 class="sh-card__title">{{ t("auth.chooseOrg") }}</h1>
      <p class="sh-card__subtitle">
        {{ t("auth.chooseOrgSubtitle", { name: tenantName, n: session.accessibleOrgs.length }) }}
      </p>
    </div>

    <p v-if="error" class="sh-card__error" role="alert">
      <v-icon size="15">error_outline</v-icon>
      <span>{{ error }}</span>
    </p>

    <!-- A list you can scan stops at a handful of rows. Past that the field
         appears and takes focus, so a name typed on arrival filters the
         rows — one real user has 48 organisations here. -->
    <label v-if="searchable" class="sh-search">
      <v-icon size="16" class="sh-search__icon">search</v-icon>
      <input
        ref="searchInput"
        v-model="query"
        class="sh-search__input"
        type="search"
        :placeholder="t('auth.findOrg')"
        autocomplete="off"
        :aria-label="t('auth.findOrg')"
      />
    </label>

    <!-- Rows are LEFT-aligned. The card centres its heading; the old version
         let that `text-align: center` cascade into the list, so every
         organisation name floated in the middle of the row while its logo
         stayed pinned to the left edge. -->
    <div class="sh-rows">
      <button
        v-for="org in visibleOrgs"
        :key="org.id"
        type="button"
        class="sh-row"
        :disabled="selecting"
        @click="choose(org)"
      >
        <LogoTile :src="org.logo || org.icon || null" :alt="org.name" :size="26" />
        <span class="sh-row__label">{{ org.name }}</span>
        <v-icon size="16" class="orgpicker__chev">chevron_right</v-icon>
      </button>
      <p v-if="searchable && !visibleOrgs.length" class="orgpicker__none">{{ t("auth.noOrgMatch", { query }) }}</p>
    </div>

    <div class="orgpicker__foot">
      <span v-if="email" class="sh-whoami">{{ t("common.signedInAs") }} <b>{{ email }}</b></span>
      <v-btn class="orgpicker__signout" variant="text" size="small" :disabled="selecting" @click="session.signOut()"
        >{{ email ? t("auth.notYouSignOut") : t("common.signOut") }}</v-btn>
    </div>
  </PreAuthPage>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useSessionStore } from "@/stores/session";
import LogoTile from "@/shell/LogoTile.vue";
import PreAuthPage from "@/shell/PreAuthPage.vue";
import type { UserOrganisation } from "@/tenant/types";

/** More rows than this and the list gets a search field. */
export const ORG_SEARCH_THRESHOLD = 6;

export default defineComponent({
  name: "OrgPicker",
  components: { LogoTile, PreAuthPage },
  setup() {
    const { t } = useI18n();
    const session = useSessionStore();
    const selecting = ref(false);
    const error = ref<string | null>(null);
    const query = ref("");
    const searchInput = ref<HTMLInputElement | null>(null);

    const tenantName = computed(() => session.tenant?.name || "Metaship");
    const branding = computed(() => session.tenant?.branding ?? null);
    const email = computed(() => session.user?.email || null);

    const searchable = computed(() => session.accessibleOrgs.length > ORG_SEARCH_THRESHOLD);
    const visibleOrgs = computed<UserOrganisation[]>(() => {
      const needle = query.value.trim().toLowerCase();
      if (!searchable.value || !needle) return session.accessibleOrgs;
      return session.accessibleOrgs.filter((org) => org.name.toLowerCase().includes(needle));
    });

    onMounted(() => searchInput.value?.focus());

    async function choose(org: UserOrganisation): Promise<void> {
      selecting.value = true;
      error.value = null;
      try {
        await session.selectOrganisation(org);
      } catch (e) {
        error.value = e instanceof Error ? e.message : t("account.switchOrgFailed");
      } finally {
        selecting.value = false;
      }
    }

    return { t, session, tenantName, branding, email, selecting, error, query, searchInput, searchable, visibleOrgs, choose };
  },
});
</script>

<style scoped>
/* `.sh-page` / `.sh-card` / `.sh-row` / `.sh-search` live in src/styles/standalone.css. */
.orgpicker__chev {
  flex: 0 0 auto;
  color: var(--sh-text-45) !important;
}
.orgpicker__none {
  margin: 0;
  padding: 14px 4px;
  text-align: center;
  font-size: 12.5px;
  color: var(--sh-text-60);
}
/* A secondary escape hatch, not a call to action — it must not out-shout the
   organisation rows above it. */
.orgpicker__signout {
  font-size: 12px;
  color: var(--sh-text-60);
}
.orgpicker__foot {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid var(--sh-hairline);
}
</style>
